import React, { ReactElement, useEffect, useState } from 'react'
import moment from 'moment'
import { TileDisplayAsValues } from '../Types'
import { STATISTICS_UNIT_TYPE_DURATION } from '../Constants'

// import './tilePreview.scss'

export type SpeedometerDialsProps = {
    min: number
    max: number
    value: number
    isGraphDialAxisReversed: boolean
    isGraphDialFacadeReversed: boolean
    tileType: number
    unitType: number
}

export enum DialClassNames {
    DASHBOARD_SPEEDOMETER_BLACK = 'black',
    DASHBOARD_SPEEDOMETER_AMBER = 'amber',
    DASHBOARD_TEMPERATURE_GAUGE = 'temp',
    DASHBOARD_FUEL_GAUGE = 'fuel',
    DASHBOARD_POWER_GAUGE = 'power',
    DASHBOARD_COLOUR_GAUGE = 'color'
}

const prepareDegValues = (
    minValueDeg: number,
    maxValueDeg: number,
    value: number,
    maxValue: number,
    isReverse: boolean
): { minValueDeg: number; maxValueDeg: number; currentValDeg: number } => {
    const oneStepDeg = (maxValueDeg - minValueDeg) / maxValue
    let currentValDeg = 0
    if (isReverse) {
        currentValDeg = maxValueDeg - value * oneStepDeg
    } else {
        currentValDeg = value * oneStepDeg + minValueDeg
    }

    return { minValueDeg, maxValueDeg, currentValDeg }
}

const updateTileType = (
    tileType: number,
    minValue: number,
    maxValue: number,
    currentValue: number,
    isGraphDialAxisReversed: boolean,
    setValueDeg: React.Dispatch<React.SetStateAction<number>>,
    setDialBG: React.Dispatch<React.SetStateAction<string>>,
    setDialPointer: React.Dispatch<React.SetStateAction<string>>
): void => {
    const imageFilePath = '/CCS/Styles/Images/Instruments/'

    const setStateValues = (
        minValueDeg: number,
        maxValueDeg: number,
        currentValDeg: number,
        imageBG: string,
        imagePointer: string
    ): void => {
        setValueDeg(currentValDeg)
        setDialBG(imageFilePath + imageBG)
        setDialPointer(imageFilePath + imagePointer)
    }

    let values
    switch (tileType) {
        case TileDisplayAsValues.DASHBOARD_SPEEDOMETER_AMBER:
            values = prepareDegValues(-191, 11, currentValue, maxValue, isGraphDialAxisReversed)

            setStateValues(
                values.minValueDeg,
                values.maxValueDeg,
                values.currentValDeg,
                'DialGraphSpeedoAmberSimpleFacade-Medium.gif',
                'DialGraphSpeedoAmberSimplePointer-Medium.gif'
            )
            break
        case TileDisplayAsValues.DASHBOARD_TEMPERATURE_GAUGE:
            values = prepareDegValues(-191, 11, currentValue, maxValue, isGraphDialAxisReversed)

            setStateValues(
                values.minValueDeg,
                values.maxValueDeg,
                values.currentValDeg,
                'DialGraphHotColdFacade-Medium.gif',
                'DialGraphHotColdPointer-Medium.gif'
            )
            break
        case TileDisplayAsValues.DASHBOARD_FUEL_GAUGE:
            values = prepareDegValues(-191, 11, currentValue, maxValue, isGraphDialAxisReversed)

            setStateValues(
                values.minValueDeg,
                values.maxValueDeg,
                values.currentValDeg,
                'DialGraphFuelGaugeFacade-Medium.gif',
                'DialGraphFuelGaugePointer-Medium.gif'
            )
            break
        case TileDisplayAsValues.DASHBOARD_POWER_GAUGE:
            values = prepareDegValues(-180, 0, currentValue, maxValue, isGraphDialAxisReversed)

            setStateValues(
                values.minValueDeg,
                values.maxValueDeg,
                values.currentValDeg,
                'DialGraphPowerGaugeFacade-Medium.gif',
                'DialGraphPowerGaugePointer-Medium.gif'
            )
            break
        case TileDisplayAsValues.DASHBOARD_COLOUR_GAUGE:
            values = prepareDegValues(-180, 0, currentValue, maxValue, isGraphDialAxisReversed)

            setStateValues(
                values.minValueDeg,
                values.maxValueDeg,
                values.currentValDeg,
                'DialGraphColourGaugeFacade-Medium.gif',
                'DialGraphColourGaugePointer-Medium.gif'
            )
            break
        case TileDisplayAsValues.DASHBOARD_SPEEDOMETER_BLACK:
        default:
            values = prepareDegValues(-191, 11, currentValue, maxValue, isGraphDialAxisReversed)

            setStateValues(
                values.minValueDeg,
                values.maxValueDeg,
                values.currentValDeg,
                'DialGraphSpeedoBlackFacade-Medium.gif',
                'DialGraphSpeedoBlackPointer-Medium.gif'
            )
            break
    }
}

const classNameLookup = (dialType: number): string => {
    switch (dialType) {
        case 1000:
            return DialClassNames.DASHBOARD_SPEEDOMETER_BLACK
        case 1010:
            return DialClassNames.DASHBOARD_SPEEDOMETER_AMBER
        case 1050:
            return DialClassNames.DASHBOARD_TEMPERATURE_GAUGE
        case 1100:
            return DialClassNames.DASHBOARD_FUEL_GAUGE
        case 1150:
            return DialClassNames.DASHBOARD_POWER_GAUGE
        case 1200:
            return DialClassNames.DASHBOARD_COLOUR_GAUGE
        default:
            return DialClassNames.DASHBOARD_SPEEDOMETER_BLACK
    }
}

const SpeedometerDials: React.FC<SpeedometerDialsProps> = ({
    min,
    max,
    value,
    isGraphDialAxisReversed,
    isGraphDialFacadeReversed,
    tileType,
    unitType
}): ReactElement => {
    const [valueDeg, setValueDeg] = useState<number>(-200)
    const [dialBG, setDialBG] = useState('')
    const [dialPointer, setDialPointer] = useState('')

    useEffect(() => {
        updateTileType(tileType, min, max, value, isGraphDialAxisReversed, setValueDeg, setDialBG, setDialPointer)
    }, [tileType, min, max, value, isGraphDialAxisReversed])

    const classes = `dial-wrapper ${classNameLookup(tileType)}`

    const formatDialValue = (numericValue: number): string => {
        if (unitType === STATISTICS_UNIT_TYPE_DURATION) {
            // The max/min value should be displayed with HH:mm:ss pattern.
            const pattern = 'HH:mm:ss'
            return moment.utc(numericValue * 1000).format(pattern)
        }
        return String(numericValue)
    }

    return (
        <div className={classes}>
            <div className='dial-bg'>
                <img className={`${isGraphDialFacadeReversed && 'dial-bg-img-reversed'}`} src={dialBG} />
            </div>
            <div className={`dial-min ${unitType === STATISTICS_UNIT_TYPE_DURATION && 'dial-min-duration'}`}>
                {formatDialValue(!isGraphDialAxisReversed ? min : max)}
            </div>
            <div className={`dial-max ${unitType === STATISTICS_UNIT_TYPE_DURATION && 'dial-max-duration'}`}>
                {formatDialValue(!isGraphDialAxisReversed ? max : min)}
            </div>
            <div className={`dial-value ${unitType === STATISTICS_UNIT_TYPE_DURATION && 'dial-value-duration'}`}>
                {formatDialValue(value)}
            </div>
            <div className='dial-indicator' style={{ transform: `rotate(${valueDeg}deg)` }}>
                <img src={dialPointer} />
            </div>
        </div>
    )
}

export default SpeedometerDials
